import { type Static, Type } from "@sinclair/typebox"
import { DOMAIN_SLUG_REGEX, USERNAME_REGEX } from "../identifier"
import { CursorQuerySchema, createPageSchema, IsoDateTimeSchema, Nullable } from "./common"

// Administración de dominios — docs/04 §3 y docs/03 §3.
// El override de branding por dominio (PUT /admin/domains/{id}/branding) usa los
// schemas de ./branding.

export const DomainStatusSchema = Type.Union([Type.Literal("active"), Type.Literal("suspended")])
export type DomainStatus = Static<typeof DomainStatusSchema>

// null = sin límite [V1]
export const DomainQuotasSchema = Type.Object({
  max_users: Nullable(Type.Integer({ minimum: 0 })),
  max_clients: Nullable(Type.Integer({ minimum: 0 })),
})
export type DomainQuotas = Static<typeof DomainQuotasSchema>

// El registro self-service se habilita por dominio (default deshabilitado).
export const RegistrationPolicySchema = Type.Object({
  enabled: Type.Boolean(),
  auto_login: Type.Boolean(),
  default_roles: Type.Array(Type.String()),
  require_email: Type.Boolean(),
  require_verification: Type.Boolean(),
})
export type RegistrationPolicy = Static<typeof RegistrationPolicySchema>

export const DomainPoliciesSchema = Type.Object({
  registration: RegistrationPolicySchema,
  mfa_required: Type.Boolean(),
})
export type DomainPolicies = Static<typeof DomainPoliciesSchema>

export const DomainSchema = Type.Object({
  id: Type.String(),
  slug: Type.String({ pattern: DOMAIN_SLUG_REGEX.source }),
  name: Type.String(),
  status: DomainStatusSchema,
  // FK diferida: maestro y dominio se crean juntos
  master_user_id: Nullable(Type.String()),
  quotas: DomainQuotasSchema,
  policies: DomainPoliciesSchema,
  // Nodo forzado del tenant: si no es null, todos los usuarios del dominio resuelven
  // siempre a este nodo (override del sharding por usuario). null = por usuario.
  node_id: Nullable(Type.String()),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
})
export type Domain = Static<typeof DomainSchema>

export const ListDomainsQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    q: Type.Optional(Type.String()),
    status: Type.Optional(DomainStatusSchema),
  }),
])
export type ListDomainsQuery = Static<typeof ListDomainsQuerySchema>

export const DomainPageSchema = createPageSchema(DomainSchema)
export type DomainPage = Static<typeof DomainPageSchema>

// La creación exige los datos del maestro inicial (se crean juntos).
export const CreateDomainRequestSchema = Type.Object({
  slug: Type.String({ pattern: DOMAIN_SLUG_REGEX.source }),
  name: Type.String({ minLength: 1 }),
  master: Type.Object({
    username: Type.String({ pattern: USERNAME_REGEX.source }),
    password: Type.String({ minLength: 1 }),
    name: Type.Optional(Type.String()),
    email: Type.Optional(Type.String({ format: "email" })),
  }),
  quotas: Type.Optional(Type.Partial(DomainQuotasSchema)),
  policies: Type.Optional(Type.Partial(DomainPoliciesSchema)),
  // Anclaje del tenant a un nodo. Por defecto (ausente o true) el dominio nuevo se
  // ancla a un nodo por consistent-hash (todos sus usuarios van ahí); false ⇒ sharding
  // por usuario. Si no hay nodos activos al crear, se omite el anclaje (se puede activar
  // luego desde la edición).
  pin_to_node: Type.Optional(Type.Boolean()),
})
export type CreateDomainRequest = Static<typeof CreateDomainRequestSchema>

// La suspensión/activación va por sus endpoints dedicados, no por PATCH.
export const UpdateDomainRequestSchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1 })),
  quotas: Type.Optional(Type.Partial(DomainQuotasSchema)),
  policies: Type.Optional(Type.Partial(DomainPoliciesSchema)),
  // Flag de anclaje del tenant a un nodo. true ⇒ se fija un nodo por consistent-hash
  // del dominio (Opción A) y todos sus usuarios van siempre ahí. false ⇒ se libera
  // (vuelve a sharding por usuario). Ausente ⇒ no se toca el anclaje.
  pin_to_node: Type.Optional(Type.Boolean()),
})
export type UpdateDomainRequest = Static<typeof UpdateDomainRequestSchema>

// POST /admin/domains/{id}/transfer-master — el nuevo maestro debe pertenecer al dominio.
export const TransferMasterRequestSchema = Type.Object({
  user_id: Type.String(),
})
export type TransferMasterRequest = Static<typeof TransferMasterRequestSchema>
